import { ButtonBS, HStack, VStack } from "@biblioteksentralen/react";
import { AtLeastOne } from "@biblioteksentralen/types";
import { getErrorMessage } from "@libry-content/common";
import { LCWork, LCWorkTriple } from "@libry-content/integrations";
import { RefObject, useCallback, useEffect, useState } from "react";
import { usePickupLocations } from "../../../../components/minside/dataFetchers/useLocations";
import { DialogLC } from "../../../../components/ModalContainer";
import { SelectLC } from "../../../../components/SelectLC";
import { useRediaPlatformContext } from "../../../../rediaPlatform/RediaPlatformProvider";
import { useTranslation } from "../../../../utils/hooks/useTranslation";
import { HoldingCounts } from "../../../hooks/useHoldingCounts";
import { useLibrarySystemBranches } from "../../../hooks/useLibrarySystemBranches";
import { useSearchConfig } from "../../../hooks/useSearchConfig";
import { ManifestationStatusForUserType } from "../../manifestations/useManifestationStatusForUser";
import { ReservationModalWorkPreview } from "../ReservationModalWorkPreview";
import { useChosenReservableHolding } from "../useChosenReservableHolding";
import { useSelectedLibrary } from "../useSelectedLibrary";
import { LoadingModal } from "./LoadingModal";
import { LoginModal } from "./LoginModal";
import { ReservationSuccessModal } from "./ReservationSuccessModal";
import { AlreadyOnLoanModal } from "./AlreadyOnLoanModal";
import { PickPartedOrWholeManifestationModal } from "./PickPartedOrWholeManifestationModal";
import { Feilmelding } from "../../../../components/minside/FeilMelding";

type CreateReservationStatus = "ready" | "loading" | "success" | "error";

type ReservationModalProps = {
  isOpen: boolean;
  onClose: () => void;
  work: LCWork;
  workTriples: AtLeastOne<LCWorkTriple>;
  representativeWorkTriple?: LCWorkTriple;
  manifestationStatusForUser: ManifestationStatusForUserType;
  onCloseFocusRef: RefObject<HTMLButtonElement | null>;
  holdingCounts?: HoldingCounts;
};

export const ReservationModal = ({
  isOpen,
  onClose,
  work,
  workTriples,
  manifestationStatusForUser,
  representativeWorkTriple,
  onCloseFocusRef,
  holdingCounts,
}: ReservationModalProps) => {
  const { t } = useTranslation();
  const { isSearchIntegrationEnabled } = useSearchConfig();
  const { rediaPlatform, user, isSessionReady } = useRediaPlatformContext();

  const {
    isilCodeFromBranchCode,
    branchCodeFromIsilCode,
    error: branchesError,
    isLoading: branchesLoading,
  } = useLibrarySystemBranches();

  const {
    onReservationsUpdate,
    isReservedByUser,
    isLoanedByUser,
    isLoading: userManifestationStatusLoading,
    isReady: userManifestationStatusReady,
    error: userManifestationStatusError,
  } = manifestationStatusForUser;

  const [createReservationStatus, setCreateReservationStatus] = useState<CreateReservationStatus>("ready");

  useEffect(() => {
    if (isOpen) setCreateReservationStatus("ready");
  }, [isOpen]);

  const handleError = useCallback(
    (error: unknown) => {
      const errorMessage = getErrorMessage(error);
      console.error(errorMessage);
      if (createReservationStatus !== "error") setCreateReservationStatus("error");
    },
    [createReservationStatus]
  );

  const pickupLocations = usePickupLocations();
  const { selectedLibrary, setSelectedLibrary, selectedLibraryLoading } = useSelectedLibrary(pickupLocations, user);

  useEffect(() => {
    if (!user || branchesLoading || selectedLibraryLoading || selectedLibrary || pickupLocations.length > 1) return;
    handleError("Could not select library although only one is available");
  }, [branchesLoading, handleError, selectedLibraryLoading, selectedLibrary, pickupLocations.length, user]);

  // Should be the same for all manifestations in this context
  const documentType = workTriples.find(({ documentType }) => documentType?.format)?.documentType;
  if (!documentType?.format) handleError(`Can't find format from ${JSON.stringify(workTriples)}`);

  const manifestationIds = workTriples.map(({ manifestationId }) => manifestationId);
  const partedTriples = work.getPartedTriples({ manifestationIds });
  const nonPartedTriples = work.getNonPartedTriples({ manifestationIds });

  const userPickingManifestationFlow = partedTriples.length > 0 && partedTriples.length + nonPartedTriples.length > 1;

  const [chosenTriple, setChosenTriple] = useState<LCWorkTriple | undefined>(undefined);
  const letUserPickManifestation = userPickingManifestationFlow && !chosenTriple;
  const manifestationsToChooseHoldingFrom = chosenTriple ? ([chosenTriple] as AtLeastOne<LCWorkTriple>) : workTriples;

  const chosenReservableHolding = useChosenReservableHolding(
    pickupLocations,
    work,
    manifestationsToChooseHoldingFrom,
    selectedLibrary,
    isilCodeFromBranchCode
  );

  const onSubmit = async () => {
    if (!rediaPlatform || !branchCodeFromIsilCode || !onReservationsUpdate || !isSearchIntegrationEnabled) {
      return handleError("Missing setup for reservation");
    }

    if (!onReservationsUpdate) {
      return handleError("Missing callback onReservationsUpdate");
    }

    if (typeof selectedLibrary?.code !== "string") {
      return handleError(`Attempted to reserve from library: ${JSON.stringify(selectedLibrary)}`);
    }

    if (!chosenReservableHolding) {
      return handleError(`Could not select item and manifestation from ${JSON.stringify(workTriples)}`);
    }

    const selectedBranchCode = selectedLibrary.code;
    const pickupLocation = {
      branch: {
        code: selectedBranchCode,
      },
    };
    const { reserveId } = chosenReservableHolding || {};

    if (!selectedBranchCode || !reserveId) {
      const missing = [
        [selectedBranchCode, "branch code"],
        [reserveId, "reserve id"],
      ].filter(([variable]) => !variable);

      return handleError(`Could not set up reservation; missing ${missing.join(" and ")}`);
    }

    setCreateReservationStatus("loading");

    try {
      const reservation = await rediaPlatform.createReservation(reserveId, pickupLocation);

      if (!reservation) return handleError("Something went wrong when creating reservation");

      console.info("Succesfully created reservation", { reservation });
      setCreateReservationStatus("success");
      onReservationsUpdate();
    } catch (err) {
      handleError(err);
    }
  };

  const handleClose = () => {
    setCreateReservationStatus("ready");
    setChosenTriple(undefined);
    onClose();
  };

  const loading =
    !isSessionReady ||
    !rediaPlatform ||
    branchesLoading ||
    selectedLibraryLoading ||
    createReservationStatus === "loading" ||
    userManifestationStatusLoading ||
    (user && !userManifestationStatusReady);

  if (loading) {
    return <LoadingModal isOpen={isOpen} handleClose={handleClose} />;
  }

  if (isSessionReady && !user) {
    return <LoginModal isOpen={isOpen} handleClose={handleClose} onCloseFocusRef={onCloseFocusRef} />;
  }

  if (
    createReservationStatus === "error" ||
    branchesError ||
    userManifestationStatusError ||
    !chosenReservableHolding ||
    !holdingCounts ||
    !selectedLibrary
  ) {
    return (
      <DialogLC heading={t("Reserver")} open={isOpen} onOpenChange={handleClose}>
        <Feilmelding
          error={
            new Error(
              JSON.stringify({
                createReservationStatus,
                branchesError: !!branchesError,
                userManifestationStatusError: !!userManifestationStatusError,
                chosenReservableHolding: !!chosenReservableHolding,
                holdingCounts: !!holdingCounts,
                selectedLibrary: !!selectedLibrary,
              })
            )
          }
        />
      </DialogLC>
    );
  }

  // Sometimes the representative manifestation used in the work view banner is different than that of the reserved manifestation.
  // In that case we don't use the image in the preview here, to avoid confusion.
  //In the case of picking a specific manifestation (with parted manifestations), we do not hide the preview image.
  const manifestationToBeReservedIsDifferentThanRepresentative =
    !!representativeWorkTriple &&
    representativeWorkTriple.manifestationId !== chosenReservableHolding?.workTriple.manifestationId;

  if (createReservationStatus === "success" || (!userPickingManifestationFlow && isReservedByUser)) {
    return (
      <ReservationSuccessModal
        isOpen={isOpen}
        handleClose={handleClose}
        holdingCounts={holdingCounts}
        reservableHolding={chosenReservableHolding}
        selectedLibrary={selectedLibrary}
        hideImage={manifestationToBeReservedIsDifferentThanRepresentative}
      />
    );
  }

  // This to cover the edge case where the user already has loaned the book but was not logged in when clicking
  // the reserve ButtonBS, so that this fact was unknown
  if (!letUserPickManifestation && isLoanedByUser) {
    return (
      <AlreadyOnLoanModal
        isOpen={isOpen}
        handleClose={handleClose}
        reservableHolding={chosenReservableHolding}
        hideImage={manifestationToBeReservedIsDifferentThanRepresentative}
      />
    );
  }

  if (letUserPickManifestation) {
    return (
      <PickPartedOrWholeManifestationModal
        partedTriples={partedTriples}
        nonPartedTriples={nonPartedTriples}
        manifestationStatusForUser={manifestationStatusForUser}
        setChosenPartedWorkTriple={setChosenTriple}
        isOpen={isOpen}
        handleClose={handleClose}
      />
    );
  }

  return (
    <DialogLC
      heading={t("Reserver")}
      open={isOpen}
      onOpenChange={handleClose}
      footer={
        <HStack justifyContent="space-between" width="100%">
          <ButtonBS variant="outline" onClick={handleClose}>
            {t("Avbryt")}
          </ButtonBS>
          <ButtonBS onClick={onSubmit}>{t("Reserver")}</ButtonBS>
        </HStack>
      }
    >
      <VStack alignItems="flex-start" gap="1.5rem">
        {!!chosenReservableHolding && (
          <ReservationModalWorkPreview
            reservableHolding={chosenReservableHolding}
            hideImage={manifestationToBeReservedIsDifferentThanRepresentative && !chosenTriple}
          />
        )}
        {pickupLocations.length > 1 && (
          <SelectLC
            marginTop="0.25rem"
            options={pickupLocations}
            value={selectedLibrary}
            onChange={(value) => value && setSelectedLibrary(value)}
            optionToLabel={(option) => option.name}
            optionToValue={(option) => option.code}
            label={t("Velg hentested")}
          />
        )}
      </VStack>
    </DialogLC>
  );
};
